import React, { useMemo, useState } from 'react';

import TitleAndDescriptionWithOrder from '@components/nycLanding/_shared/titleAndDescriptionWithOrder';
import { NYCLandingType } from '@utils/types/landings';
import ImageWithLoader from '@components/_shared/imageWithLoader';
import { CircledChevronLeft, CircledChevronRight } from '@assets/icons';
import { colorsByTheme } from '@utils/constants/colors';

import styles from './styles.module.scss';

type Props = {
  order: number;
} & NYCLandingType['successStories'];

const SuccessStories = (props: Props) => {
  const { title, description, items, order } = props;
  const [currentSlideIndex, setCurrentListingIndex] = useState(0);

  const { currentSlide } = useMemo(
    () => ({
      currentSlide: items[currentSlideIndex],
    }),
    [currentSlideIndex, items?.length]
  );

  const onBackClick = () => {
    setCurrentListingIndex(
      currentSlideIndex - 1 === -1 ? items.length - 1 : currentSlideIndex - 1
    );
  };

  const onNextClick = () => {
    setCurrentListingIndex(
      currentSlideIndex + 1 === items.length ? 0 : currentSlideIndex + 1
    );
  };

  const renderSlideInfo = (slide: (typeof items)[0]) => (
    <>
      <div className={styles.slideInfo__title}>{slide.title}</div>
      <div className={styles.slideInfo__stats}>
        {slide.items.map((s) => (
          <div key={s._key}>
            <div className={styles.slideInfo__value}>{s.value}</div>
            <div className={styles.slideInfo__label}>{s.label}</div>
          </div>
        ))}
      </div>

      {!!slide.quote?.quote && (
        <div className={styles.slideInfo__quote}>
          <div className={styles.slideInfo__quote__value}>
            {slide.quote.quote}
          </div>

          <div className={styles.slideInfo__quote__author}>
            {slide.quote.authorAvatar && (
              <ImageWithLoader
                containerStyle={styles.slideInfo__quote__author__avatar}
                src={slide.quote.authorAvatar}
                alt="author avatar"
                sizes="48px"
              />
            )}

            <div className={styles.slideInfo__label}>
              {`${slide.quote.authorName}, ${slide.quote.authorTitle}`}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className={styles.successStories}>
      <TitleAndDescriptionWithOrder
        title={title}
        description={description}
        order={order}
      />

      <div className={styles.successStories__wrapper}>
        <div className={styles.currentSlide}>
          <div className={styles.currentSlide__info}>
            {renderSlideInfo(currentSlide)}
          </div>
          <ImageWithLoader
            containerStyle={styles.currentSlide__image}
            src={currentSlide.image}
            alt={currentSlide.title}
            sizes="75vw"
          />
        </div>
      </div>

      <div className={styles.arrows}>
        <CircledChevronLeft
          stroke={colorsByTheme['dark'].textPrimaryColor}
          onClick={onBackClick}
        />
        <CircledChevronRight
          stroke={colorsByTheme['dark'].textPrimaryColor}
          onClick={onNextClick}
        />
      </div>
    </div>
  );
};

export default SuccessStories;
